import { Link, Outlet, useLocation, useNavigate } from "react-router";
import {
  Eye,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import logo from "@/assets/logo.svg";
import { Seo } from "./Seo";

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isRestoringAdmin, setIsRestoringAdmin] = useState(false);
  const { user, logout, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isImpersonating = user?.impersonation?.active === true;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleStopImpersonation = async () => {
    setIsRestoringAdmin(true);

    try {
      await stopImpersonation();
      navigate("/admin/projects");
    } finally {
      setIsRestoringAdmin(false);
    }
  };

  const navigation = [
    {
      name: "Projektbereich",
      href: "/dashboard",
      icon: <LayoutDashboard size={20} />,
    },
    {
      name: "Vertrag & Abrechnung",
      href: "/dashboard/vertrag-abrechnung",
      icon: <CreditCard size={20} />,
    },
  ];

  const isActive = (href: string) => {
    return href === "/dashboard"
      ? location.pathname === "/dashboard"
      : location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Seo
        title="Kundenbereich"
        description="Geschützter Kundenbereich von mutzur."
        path={location.pathname}
        noIndex
      />
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 z-40 border-b border-gray-300 bg-white shadow-sm">
        <div className="h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="rounded-lg p-2 text-gray-900 transition-colors hover:bg-gray-200 lg:hidden"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo */}
            <Link to="/">
              <img src={logo} alt="mutzur" className="h-4.5 ml-5" />
            </Link>
          </div>

          {/* User Info */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-gray-700">{user?.company}</p>
            </div>
            <button
              onClick={handleLogout}
              className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-950"
              title="Abmelden"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 bottom-0 z-30 w-64 border-r border-gray-300 bg-white transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className="p-4 space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.href)
                  ? "bg-black text-white"
                  : "text-gray-800 hover:bg-gray-200 hover:text-gray-950"
              }`}
            >
              {item.icon}
              <span>{item.name}</span>
            </Link>
          ))}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="pt-16 lg:pl-64">
        <div className="p-4 sm:p-6 lg:p-8">
          {isImpersonating ? (
            <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm text-blue-950 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <Eye size={18} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Kundenansicht im Admin-Modus</p>
                  <p className="mt-1 leading-relaxed">
                    Sie sehen den Kundenbereich von {user?.name}. Bestellungen und Revisionen
                    bleiben deaktiviert, Uploads koennen Sie hier jedoch zu Testzwecken ausfuehren.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  void handleStopImpersonation();
                }}
                disabled={isRestoringAdmin}
                className="inline-flex items-center justify-center rounded-xl border border-blue-300 bg-white px-4 py-2.5 font-medium text-blue-950 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isRestoringAdmin ? "Wechselt zurück..." : "Zurück zum Admin"}
              </button>
            </div>
          ) : null}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
